import { AnimatePresence, motion } from "motion/react";
import { FastForward, Maximize2, Pause, Play, Rewind, Volume2, VolumeX } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { spring } from "../lib/motion";
import { applyVolume, RATES, saveVolume, useSavedVolume } from "../lib/volume";
import { clamp, cn, formatDuration } from "../lib/utils";
import type { FrameDrawing as FrameDrawingValue } from "../types";
import { FrameDrawing } from "./FrameDrawing";

export interface Marker {
  t: number;
  tone: "wait" | "done" | "rework";
  label?: string;
}

/**
 * Плеер 9:16. Если есть src (настоящее видео, загруженное монтажёром) — играет его,
 * иначе «проигрывает» постер с живым таймлайном.
 */
export function VideoPlayer({
  src,
  poster,
  duration,
  markers = [],
  onTime,
  seekTo,
  header,
  subtitle,
  size = "md",
  className,
  annotation,
  drawingEnabled = false,
  onDrawingChange,
}: {
  src?: string;
  poster: string;
  duration: number;
  markers?: Marker[];
  onTime?: (t: number) => void;
  seekTo?: { t: number; n: number } | null;
  header?: ReactNode;
  subtitle?: string;
  /** sm — ниже по высоте, чтобы влезть в колонку страницы */
  size?: "md" | "sm";
  className?: string;
  annotation?: FrameDrawingValue | null;
  drawingEnabled?: boolean;
  onDrawingChange?: (drawing: FrameDrawingValue) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const maxW = size === "sm" ? 234 : 300;
  const [vw, setVw] = useState(maxW);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(duration);
  // Громкость общая на все ролики и запоминается: выставил раз — так и стоит.
  const sound = useSavedVolume();
  const silent = sound.muted || sound.volume === 0;
  const speed = sound.rate;
  const nextSpeed = () => saveVolume({ rate: RATES[(RATES.indexOf(speed as (typeof RATES)[number]) + 1) % RATES.length] });

  // Кадр 9:16 подстраивается под высоту — шапка с версиями и кнопки не должны обрезаться
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => {
      const chrome = 40 + 16 + 24 + 14 * 2 + (header ? 28 + 14 : 0);
      setVw(Math.max(150, Math.min(maxW, Math.floor(((el.clientHeight - chrome) * 9) / 16))));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxW, header]);

  useEffect(() => setDur(duration), [duration]);
  useEffect(() => onTime?.(time), [time, onTime]);

  // «Проигрывание» постера
  useEffect(() => {
    if (src || !playing) return;
    let raf = 0;
    let last = performance.now();
    const tick = (ts: number) => {
      const dt = (ts - last) / 1000;
      last = ts;
      setTime((t) => {
        const nt = t + dt * speed;
        if (nt >= dur) {
          setPlaying(false);
          return dur;
        }
        return nt;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, src, speed, dur]);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    el.playbackRate = speed;
  }, [speed]);

  useEffect(() => applyVolume(video.current, sound), [sound, src]);

  useEffect(() => {
    if (!drawingEnabled) return;
    video.current?.pause();
    setPlaying(false);
  }, [drawingEnabled]);

  const toggleMute = () => {
    // Включили звук, а громкость стояла на нуле — иначе кнопка «не работает».
    if (silent) saveVolume({ muted: false, volume: sound.volume === 0 ? 0.5 : sound.volume });
    else saveVolume({ muted: true });
  };

  const seek = (t: number) => {
    const nt = clamp(t, 0, dur);
    setTime(nt);
    if (video.current) video.current.currentTime = nt;
  };

  useEffect(() => {
    if (seekTo) seek(seekTo.t);
  }, [seekTo?.n]);

  const toggle = () => {
    const el = video.current;
    if (el) {
      if (el.paused) {
        if (el.ended) el.currentTime = 0;
        void el.play();
      } else el.pause();
      return;
    }
    if (!playing && time >= dur) setTime(0);
    setPlaying((p) => !p);
  };

  const seekFromPointer = (clientX: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r) return;
    seek(((clientX - r.left) / r.width) * dur);
  };

  const pct = dur > 0 ? time / dur : 0;

  return (
    <div
      ref={box}
      className={cn("flex h-full shrink-0 flex-col items-center justify-center gap-3.5 bg-player p-5", className)}
      style={{ ["--vw" as string]: `${vw}px`, width: vw + 40 }}
    >
      {header && <div className="flex w-full items-center gap-1.5">{header}</div>}
      <div className="group relative h-[calc(var(--vw)*16/9)] w-[var(--vw)] shrink-0 overflow-hidden rounded-[20px] bg-black" onClick={() => !drawingEnabled && toggle()}>
        {src ? (
          <video
            ref={video}
            src={src}
            poster={poster}
            playsInline
            className="size-full object-cover"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => {
              applyVolume(e.currentTarget, sound);
              if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration);
            }}
          />
        ) : (
          <motion.img
            src={poster}
            alt=""
            draggable={false}
            className="size-full object-cover"
            animate={{ scale: 1 + pct * 0.08 }}
            transition={{ ease: "linear", duration: 0.2 }}
          />
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-black/65" />
        {subtitle && !src && (
          <div className="pointer-events-none absolute inset-x-4 bottom-12 text-center text-base font-bold text-white drop-shadow">{subtitle}</div>
        )}
        {(annotation || drawingEnabled) && <FrameDrawing value={annotation} editable={drawingEnabled} onChange={onDrawingChange} />}
        <AnimatePresence>
          {!playing && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={spring.snappy}
              className="absolute left-1/2 top-1/2 -ml-8 -mt-8 grid size-16 place-items-center rounded-full bg-white/20 text-white backdrop-blur-md"
            >
              <Play className="ml-0.5 size-6 fill-white" />
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div
        ref={track}
        className="relative h-4 w-[var(--vw)] shrink-0 cursor-pointer touch-none"
        onPointerDown={(e) => {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          seekFromPointer(e.clientX);
        }}
        onPointerMove={(e) => e.buttons === 1 && seekFromPointer(e.clientX)}
      >
        <div className="absolute inset-x-0 top-1.5 h-1 rounded-full bg-white/25" />
        <div className="absolute left-0 top-1.5 h-1 rounded-full bg-white" style={{ width: `${pct * 100}%` }} />
        {markers.map((m, i) => (
          <button
            key={i}
            type="button"
            title={m.label}
            onPointerDown={(e) => {
              e.stopPropagation();
              seek(m.t);
            }}
            className={cn(
              "absolute top-1 size-2 -translate-x-1/2 rounded-full ring-2 ring-player transition-transform hover:scale-150",
              m.tone === "done" ? "bg-done" : m.tone === "rework" ? "bg-rework" : "bg-wait",
            )}
            style={{ left: `${(m.t / Math.max(dur, 1)) * 100}%` }}
          />
        ))}
        <span className="absolute top-[1px] size-3.5 -translate-x-1/2 rounded-full bg-white shadow" style={{ left: `${pct * 100}%` }} />
      </div>

      <div className="flex w-[var(--vw)] shrink-0 items-center justify-between text-white">
        <div className={cn("flex items-center", size === "sm" ? "gap-2" : "gap-3")}>
          <button type="button" onClick={toggle} className="hover:opacity-80">
            {playing ? <Pause className="size-[18px] fill-white" /> : <Play className="size-[18px] fill-white" />}
          </button>
          {vw >= 260 && (
            <>
              <button type="button" onClick={() => seek(time - 5)} className="text-white/70 hover:text-white">
                <Rewind className="size-[18px]" />
              </button>
              <button type="button" onClick={() => seek(time + 5)} className="text-white/70 hover:text-white">
                <FastForward className="size-[18px]" />
              </button>
            </>
          )}
          <span className="whitespace-nowrap font-mono text-xs tabular-nums text-white/70">
            {/* Узкому плееру место нужнее под громкость; длительность и так в шапке. */}
            {formatDuration(time)}{vw >= 320 ? ` / ${formatDuration(dur)}` : ""}
          </span>
        </div>
        <div className={cn("flex items-center", size === "sm" ? "gap-2" : "gap-2.5")}>
          {(
            <button type="button" onClick={nextSpeed} aria-label="Скорость" className="w-9 text-xs font-semibold text-white/70 hover:text-white">
              {speed}×
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={toggleMute} aria-label={silent ? "Включить звук" : "Выключить звук"} className="text-white/70 hover:text-white">
              {silent ? <VolumeX className="size-[18px]" /> : <Volume2 className="size-[18px]" />}
            </button>
            {vw >= 240 && (
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={silent ? 0 : sound.volume}
                onChange={(e) => saveVolume({ volume: Number(e.target.value), muted: Number(e.target.value) === 0 })}
                aria-label="Громкость"
                className="h-1 w-12 cursor-pointer accent-white"
              />
            )}
          </div>
          <button type="button" onClick={() => video.current?.requestFullscreen?.()} className="text-white/70 hover:text-white">
            <Maximize2 className="size-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function VersionPills({ versions, active, onChange, meta }: { versions: number[]; active: number; onChange?: (n: number) => void; meta?: string }) {
  return (
    <>
      {versions.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          className={cn(
            "h-7 rounded-full px-3 text-xs font-semibold transition-colors",
            n === active ? "bg-white text-black" : "border border-white/25 text-white/70 hover:text-white",
          )}
        >
          v{n}
        </button>
      ))}
      <span className="flex-1" />
      {meta && <span className="text-xs text-white/70">{meta}</span>}
    </>
  );
}
